const MAX_TIMINGS = 50;
const RECENT_EDITOR_UPDATE_MS = 5000;

let nextTimingId = 0;
let lastEditorValueUpdate = null;
const activeTimings = new Map();

function now() {
  return globalThis.performance?.now ? globalThis.performance.now() : Date.now();
}

function targetWindow() {
  return globalThis.window || globalThis;
}

function markPerformance(name) {
  try {
    globalThis.performance?.mark?.(`front-my-end:${name}`);
  } catch {
    // Performance marks are diagnostic-only.
  }
}

function duration(profile, start, end) {
  if (!profile?.marks || !(start in profile.marks) || !(end in profile.marks)) {
    return null;
  }
  return Math.round((profile.marks[end] - profile.marks[start]) * 10) / 10;
}

function timingStore() {
  const win = targetWindow();
  if (!Array.isArray(win.__FRONT_MY_END_PREVIEW_TIMINGS__)) {
    win.__FRONT_MY_END_PREVIEW_TIMINGS__ = [];
  }
  return win.__FRONT_MY_END_PREVIEW_TIMINGS__;
}

export function recordEditorValueUpdate(source, update) {
  const startedAt = now();
  update();
  const finishedAt = now();
  lastEditorValueUpdate = {
    source,
    startedAt,
    finishedAt,
    durationMs: Math.round((finishedAt - startedAt) * 10) / 10,
  };
}

export function startPreviewTiming(reason = 'run') {
  const startedAt = now();
  const editorValueUpdateAgeMs = lastEditorValueUpdate
    ? Math.round((startedAt - lastEditorValueUpdate.finishedAt) * 10) / 10
    : null;
  const profile = {
    id: nextTimingId += 1,
    reason,
    startedAt,
    marks: {
      runStart: startedAt,
    },
    editorValueUpdate: (
      lastEditorValueUpdate && editorValueUpdateAgeMs <= RECENT_EDITOR_UPDATE_MS
        ? { ...lastEditorValueUpdate, ageMs: editorValueUpdateAgeMs }
        : null
    ),
  };

  activeTimings.set(profile.id, profile);
  if (activeTimings.size > MAX_TIMINGS) {
    activeTimings.delete(activeTimings.keys().next().value);
  }
  markPerformance(`${profile.id}:run-start`);
  return profile;
}

export function markPreviewTiming(profile, label) {
  if (!profile) return;
  profile.marks[label] = now();
  markPerformance(`${profile.id}:${label}`);
}

export function markPreviewTimingById(timingId, label) {
  const profile = activeTimings.get(timingId);
  if (!profile) return;
  markPreviewTiming(profile, label);
}

export function finishPreviewTimingById(timingId) {
  const profile = activeTimings.get(timingId);
  if (!profile) return null;

  markPreviewTiming(profile, 'iframe-render-complete');
  activeTimings.delete(timingId);

  const finishedAt = profile.marks['iframe-render-complete'];
  const record = {
    id: profile.id,
    reason: profile.reason,
    capturedAt: new Date().toISOString(),
    editorValueUpdateMs: profile.editorValueUpdate?.durationMs ?? null,
    editorValueUpdateToRunMs: profile.editorValueUpdate?.ageMs ?? null,
    documentBuildMs: duration(profile, 'document-build-start', 'document-build-end'),
    sandboxMessageSendMs: duration(profile, 'sandbox-message-send-start', 'sandbox-message-send-end'),
    iframeRenderWaitMs: duration(profile, 'sandbox-message-send-end', 'iframe-render-complete'),
    totalRunToRenderMs: Math.round((finishedAt - profile.startedAt) * 10) / 10,
  };

  const store = timingStore();
  store.push(record);
  if (store.length > MAX_TIMINGS) {
    store.splice(0, store.length - MAX_TIMINGS);
  }

  if (typeof CustomEvent === 'function') {
    targetWindow().dispatchEvent?.(
      new CustomEvent('front-my-end-preview-timing', { detail: record }),
    );
  }
  console.debug?.('[Front-my-End] Preview timing', record);
  return record;
}
